import { Action, getModule, Module, VuexModule } from 'vuex-module-decorators';
import store from '@/store/index';
import { WS_URL as ApiUrl } from '@/config';
import axios from 'axios';
import { NotificationService } from '@/services/notification-service';

export type MailSchedulerRoutine = 'Newsletters' | 'Announcement';

export interface MailSchedulerOption {
  index: number;
  name: string;
}

export interface MailSchedulerOptions {
  newsletters: MailSchedulerOption[];
  announcements: MailSchedulerOption[];
}

export interface MailSchedulerRunResult {
  routine: MailSchedulerRoutine;
  dryRun: boolean;
  selectedName: string;
  recipients: string[];
  stats: { [key: string]: number };
  totalMails: number;
}

export interface RunMailSchedulerRequest {
  routine: MailSchedulerRoutine;
  index: number;
  send: boolean;
}

export interface SendSampleMailRequest {
  routine: MailSchedulerRoutine;
  index: number;
  email: string;
}

export type MailSchedulerJobStatus = 'Pending' | 'Running' | 'Completed' | 'Failed';

export interface MailSchedulerRunStartResult {
  jobId: string;
}

export interface MailSchedulerRunProgress {
  jobId: string;
  status: MailSchedulerJobStatus;
  routine: MailSchedulerRoutine;
  dryRun: boolean;
  selectedName: string;
  total: number;
  processed: number;
  sent: number;
  skipped: number;
  currentRecipient: string | null;
  error: string | null;
  percentComplete: number;
  result: MailSchedulerRunResult | null;
}

export interface MailPreview {
  name: string;
  html: string;
}

@Module({ dynamic: true, namespaced: true, store, name: 'mailSender' })
export class MailSenderModule extends VuexModule {
  @Action
  async getOptions(): Promise<MailSchedulerOptions> {
    try {
      const response = await axios.get<MailSchedulerOptions>(`${ApiUrl}/api/MailScheduler/options`);
      return response.data;
    } catch (error) {
      NotificationService.NotifyError('Could not load the mail sender options.');
      return { newsletters: [], announcements: [] };
    }
  }

  @Action
  async run(request: RunMailSchedulerRequest): Promise<MailSchedulerRunResult | null> {
    try {
      const response = await axios.post<MailSchedulerRunResult>(`${ApiUrl}/api/MailScheduler/run`, request);

      if (request.send) {
        const total = response.data.totalMails;
        NotificationService.NotifySuccess(`${total} email${total === 1 ? '' : 's'} sent.`);
      }

      return response.data;
    } catch (error) {
      NotificationService.NotifyError('Could not run the mail sender.');
      return null;
    }
  }

  @Action
  async startRun(request: RunMailSchedulerRequest): Promise<MailSchedulerRunStartResult | null> {
    try {
      const response = await axios.post<MailSchedulerRunStartResult>(`${ApiUrl}/api/MailScheduler/run-async`, request);
      return response.data;
    } catch (error) {
      NotificationService.NotifyError('Could not start the mail sender.');
      return null;
    }
  }

  @Action
  async getProgress(jobId: string): Promise<MailSchedulerRunProgress | null> {
    try {
      const response = await axios.get<MailSchedulerRunProgress>(`${ApiUrl}/api/MailScheduler/progress/${jobId}`);
      return response.data;
    } catch (error) {
      return null;
    }
  }

  @Action
  async sendSample(request: SendSampleMailRequest): Promise<boolean> {
    try {
      await axios.post(`${ApiUrl}/api/MailScheduler/send-sample`, request);
      NotificationService.NotifySuccess(`Sample sent to ${request.email}.`);
      return true;
    } catch (error) {
      const message =
        axios.isAxiosError(error) && error.response && typeof error.response.data === 'string'
          ? `Could not send the sample: ${error.response.data}`
          : 'Could not send the sample.';
      NotificationService.NotifyError(message);
      return false;
    }
  }

  @Action
  async getPreview(request: { routine: MailSchedulerRoutine; index: number }): Promise<MailPreview | null> {
    try {
      const response = await axios.get<MailPreview>(`${ApiUrl}/api/MailScheduler/preview`, {
        params: { routine: request.routine, index: request.index }
      });
      return response.data;
    } catch (error) {
      const message =
        axios.isAxiosError(error) && error.response
          ? `Could not load the newsletter preview (${error.response.status}).`
          : 'Could not load the newsletter preview.';
      NotificationService.NotifyError(message);
      return null;
    }
  }
}

export const MailSenderStore = getModule(MailSenderModule);
